// Все union-типы домена + zod-схемы для них.
// Массив констант — источник правды, схема и тип выводятся из него.
import { z } from 'zod'

// Тип узы (§1 04_links.md). Определяет тон Луни, линзу совместимости и порог маячка.
export const LINK_TYPES = [
  'friend',
  'partner',
  'mother',
  'father',
  'sibling',
  'relative',
  'other',
] as const
export const linkTypeSchema = z.enum(LINK_TYPES)
export type LinkType = z.infer<typeof linkTypeSchema>

// Тип разовой проверки совместимости (§05_compatibility.md, шаг 1). Шире, чем LinkType —
// добавлены «краш» и «бывший», которых нет среди уз.
// Порядок — он же порядок чипов на шаге 1: сначала близкие, потом семья, потом работа.
export const COMPAT_TYPES = [
  'friend',
  'partner',
  'crush',
  'ex',
  'mother',
  'father',
  'sibling',
  'relative',
  'colleague',
  'boss',
] as const
export const compatTypeSchema = z.enum(COMPAT_TYPES)
export type CompatType = z.infer<typeof compatTypeSchema>

// Точность проверки совместимости: по дате рождения или только по знаку Солнца.
// Это то, что человек ВЫБРАЛ в форме. Чем расчёт в итоге располагал — отдельная величина,
// `CompatPrecision` ниже: дату можно знать, а время и город — нет.
export const COMPAT_ACCURACIES = ['date', 'sign'] as const
export const compatAccuracySchema = z.enum(COMPAT_ACCURACIES)
export type CompatAccuracy = z.infer<typeof compatAccuracySchema>

// Чем расчёт располагал по КАЖДОЙ из двух карт. Считается отдельно для себя и для второго
// человека, потому что деградация у сторон разная: свои данные обычно полные, чужие — нет.
// Порядок — от точного к грубому, на нём держится `worseCompatPrecision` (domain/compatPrecision).
//
//   exact        — дата, время и город: момент рождения переводится в UTC однозначно,
//                  считается и асцендент;
//   time_no_city — дата и время без города: момент собран по зоне по умолчанию, асцендента нет;
//   date_only    — одна дата: берётся местный полдень, погрешность Луны до ±6.5°;
//   sign_only    — известен только знак Солнца: градусов нет вовсе.
export const COMPAT_PRECISIONS = ['exact', 'time_no_city', 'date_only', 'sign_only'] as const
export const compatPrecisionSchema = z.enum(COMPAT_PRECISIONS)
export type CompatPrecision = z.infer<typeof compatPrecisionSchema>

// Отметка состояния дня: 1 Тяжко … 4 Сияю.
export const MOOD_VALUES = [1, 2, 3, 4] as const
export const moodValueSchema = z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4)])
export type MoodValue = z.infer<typeof moodValueSchema>

// Ступень близости связи по теплоте (см. domain/teplo.ts).
export const TIERS = ['near', 'mid', 'far'] as const
export const tierSchema = z.enum(TIERS)
export type Tier = z.infer<typeof tierSchema>

// Состояние узла на полотне уз — шире, чем Tier: добавлены служебные состояния
// («я сама», ожидание приглашения, отпуск, маячок, реплика Луни).
export const NODE_STATES = [
  'me',
  'near',
  'mid',
  'far',
  'beacon',
  'waiting',
  'vacation',
  'lunya',
] as const
export const nodeStateSchema = z.enum(NODE_STATES)
export type NodeState = z.infer<typeof nodeStateSchema>

// Тип события в «Что прилетело».
export const NOTIFICATION_KINDS = ['ray', 'lunya', 'milestone', 'link'] as const
export const notificationKindSchema = z.enum(NOTIFICATION_KINDS)
export type NotificationKind = z.infer<typeof notificationKindSchema>

// Ответ на прогноз дня: сбылось / мимо.
export const FORECAST_ANSWERS = ['hit', 'miss'] as const
export const forecastAnswerSchema = z.enum(FORECAST_ANSWERS)
export type ForecastAnswer = z.infer<typeof forecastAnswerSchema>

// Пол — для склонений в текстах Луни и парных ярлыков.
export const GENDERS = ['f', 'm'] as const
export const genderSchema = z.enum(GENDERS)
export type Gender = z.infer<typeof genderSchema>

// 12 знаков зодиака, в порядке зодиакального круга от Овна.
export const ZODIAC_SIGNS = [
  'aries',
  'taurus',
  'gemini',
  'cancer',
  'leo',
  'virgo',
  'libra',
  'scorpio',
  'sagittarius',
  'capricorn',
  'aquarius',
  'pisces',
] as const
export const zodiacSignSchema = z.enum(ZODIAC_SIGNS)
export type ZodiacSign = z.infer<typeof zodiacSignSchema>

// Семь тел, которые уходят в контракт натальной карты и в синастрию: два светила
// (Солнце, Луна) и пять планет (Меркурий, Венера, Марс, Юпитер, Сатурн).
//
// ЭТОТ СПИСОК НЕ РАСШИРЯЕТСЯ. На нём держатся `NatalPlanetPosition` в ответе карты,
// колонка `planetsJson` в БД и `CompatPositions` в синастрии. Синастрия считает ровно пять
// точек, и её орбы, веса линз и растяжка процента на диапазон 30..100 настроены именно на этот
// состав: добавление медленных планет молча сдвинуло бы все уже показанные проценты.
// Всё, что шире, живёт ниже в `CHART_POINTS` — отдельным типом, а не приросшим к этому.
export const PLANETS = ['sun', 'moon', 'mercury', 'venus', 'mars', 'jupiter', 'saturn'] as const
export const planetSchema = z.enum(PLANETS)
export type Planet = z.infer<typeof planetSchema>

// Тела, положение которых даёт эфемерида: два светила и восемь планет.
// Плутон в номенклатуре МАС — карликовая планета; здесь имеется в виду астрологическая
// традиция, в которой он планета, и переименовывать её под астрономию мы не вправе.
//
// Собран из `PLANETS`, а не переписан списком: так компилятор гарантирует, что любой `Planet`
// остаётся `ChartBody`, и семь тел карты не разъедутся с семью телами контракта.
export const CHART_BODIES = [...PLANETS, 'uranus', 'neptune', 'pluto'] as const
export const chartBodySchema = z.enum(CHART_BODIES)
export type ChartBody = z.infer<typeof chartBodySchema>

// Лунные узлы — НЕ тела: это точки пересечения орбиты Луны с эклиптикой.
// Южный всегда ровно напротив северного, отдельного расчёта у него нет.
export const CHART_NODES = ['north_node', 'south_node'] as const

// Лилит (Чёрная Луна) — тоже не тело, а самая дальняя точка лунной орбиты. Никакого объекта
// там нет, и в разборе это говорится вслух: слово популярное, и человек вправе думать иначе.
export const CHART_LILITH = ['lilith'] as const

// Угловые точки — тоже не тела: асцендент и MC считаются из времени и координат места,
// без них не существуют вовсе (docs/domain.md §8, словарь: «не шестая планета, не светило»).
export const CHART_ANGLES = ['asc', 'mc'] as const

// Всё, чем располагает геометрия ОДНОЙ карты. Порядок — привычный порядок колонки в карте:
// светила, планеты по удалению от Солнца, узлы, углы.
export const CHART_POINTS = [
  ...CHART_BODIES,
  ...CHART_NODES,
  ...CHART_LILITH,
  ...CHART_ANGLES,
] as const
export const chartPointSchema = z.enum(CHART_POINTS)
export type ChartPoint = z.infer<typeof chartPointSchema>
